import React, { useState } from "react";

// Redux/Hooks/APIs/Utils
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { IValuatorRegisterForm } from "../valuatorRegisterContext";
import { toast } from "react-toastify";
import { parameterApi } from "@/main/api";
import { supervisionAssignmentRequest } from "@/main/models/request/supervisionAssignment";
import { ParameterTypesResponse } from "@/main/models/response/parameterTypesResponse";
import { ParameterTypeValueEnum } from "@/main/services/enum/ParameterTypeValueEnum";
import { resetReducerParameterTypeValue } from "@/main/store/parameterTypeValue";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { checkParametersChange } from "@/main/components/shared/tabs/insurer/tabSettingRegister/utils";

// Components
import { Switch, TableCell } from "@mui/material";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import SectionParameter from "@/main/components/shared/tabs/insurer/tabSettingRegister/sectionParameter/sectionParameter";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import CompanyManufacturer from "@/main/components/shared/companyManufacturer/companyManufacturer";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

// Styles
import * as SP from "@/main/components/shared/styles";
import * as S from "./styles";
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";
import SupervisorBodyShopGrid from "@/main/components/shared/supervisorBodyshop/supervisorBodyshopGrid/supervisorBodyshopGrid";
import { supervisionAssignmentApi } from "@/main/api/supervisionAssignmentApi";

interface ITabValuatorClaimSettings {
	parameterTypes: ParameterTypesResponse[];
	setDataParameterTypes: React.Dispatch<React.SetStateAction<ParameterTypesResponse[]>>;
	guidValuator: string | null;
	guidCompany: string;
	actAsSupervisor: boolean;
	SetActAsSupervisor: React.Dispatch<React.SetStateAction<boolean>>;
}

function TabValuatorClaimSettings({
	parameterTypes,
	setDataParameterTypes,
	guidValuator,
	guidCompany,
	actAsSupervisor,
	SetActAsSupervisor,
}: ITabValuatorClaimSettings) {
	// #region utils
	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	const lowercaseValues = (parameterTypes: ParameterTypesResponse[]) => {
		return parameterTypes.map((data) => ({
			...data,
			parameters: data.parameters.map((parameter) => {
				if (data.idParameterTypeValue === ParameterTypeValueEnum.Boolean) {
					return {
						...parameter,
						value: parameter.value.toLowerCase(),
					};
				} else {
					return {
						...parameter,
					};
				}
			}),
		}));
	};
	// #endregion

	// #region hooks
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const methods = useFormContext<IValuatorRegisterForm>();
	const watcherCountry = methods.watch("country");
	// #region

	// #region states
	const [dataParametersScreen, setDataParametersScreen] = useState<ParameterTypesResponse[]>(
		parameterTypes ? lowercaseValues(parameterTypes) : [],
	);
	const [isLoadingSaveSettings, setIsLoadingSaveSettings] = useState<boolean>(false);
	// #endregion

	// #region handlers
	function handleBack() {
		navigate(-1);
	}

	async function handleSaveSettings() {
		setIsLoadingSaveSettings(true);
		const requestSaveParameters: { guid: string; value: string }[] = [];

		dataParametersScreen.forEach((x: any) => {
			x.parameters.map((y: any) => {
				return requestSaveParameters.push({
					guid: y.guid,
					value: y.value,
				});
			});
		});

		try {
			let filesRequest = undefined;
			let guidReason = null;
			let reasonDescription = null;

			if (guidValuator) {
				const resultConfirm = await getConfirmationReason(
					getTranslateByKey("information.confirmChangeTitle"),
					ReasonTypeEnum.ChangeCompany,
				);

				if (!resultConfirm.confirmed) {
					setIsLoadingSaveSettings(false);
					return;
				}

				if (resultConfirm.files && resultConfirm.files.length > 0) {
					filesRequest = await Promise.all(
						resultConfirm.files.map(async (item) => {
							const base64DataUrl = await toBase64(item);
							return {
								fileName: item.name,
								contentType: item.type,
								fileBase64: base64DataUrl as string,
							};
						}),
					);
				}

				guidReason = resultConfirm.reason ? String(resultConfirm.reason.key) : null;
				reasonDescription = resultConfirm.observation || null;
			}

			const payload = {
				parameters: requestSaveParameters,
				changeJustification: {
					guidReason,
					reasonDescription,
					filesUpload: filesRequest,
				},
			};

			await parameterApi.saveParameter(payload);

			toast.success(getTranslateByKey("information.parametersSavedSuccessfully"));
			dispatch(resetReducerParameterTypeValue());
			setDataParameterTypes(dataParametersScreen);
			setIsLoadingSaveSettings(false);
		} finally {
			setIsLoadingSaveSettings(false);
		}
	}

	async function handleActSupervisor(event: React.ChangeEvent<HTMLInputElement>, actSupervisor: boolean) {
		if (isNullUndefined(guidValuator)) return;

		if (!actSupervisor) {
			const message = getTranslateByKey("information.confirmRemoveActAsSupervisor");
			const isConfirmed = await getConfirmation(message);

			if (!isConfirmed) {
				event.preventDefault();
				return;
			}
		}

		const resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) {
			if (!actSupervisor) {
				// user cancelled after answering the first modal
			} else {
				event.preventDefault();
			}
			return;
		}

		let filesRequest = undefined;
		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		const request = {
			guidCompany: guidCompany,
			actAsSupervisor: actSupervisor,
			changeJustification: {
				guidReason: resultConfirm.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm.observation || null,
				filesUpload: filesRequest,
			},
		} as supervisionAssignmentRequest;

		supervisionAssignmentApi
			.saveActAsSupervisor(request)
			.then(() => {
				SetActAsSupervisor(actSupervisor);
				toast.success(
					actAsSupervisor
						? getTranslateByKey("information.removeSaveActAsSupervisorSuccessfully")
						: getTranslateByKey("information.setSaveActAsSupervisorSuccessfully"),
				);
			})
			.catch(() => {
				toast.error(
					actAsSupervisor
						? getTranslateByKey("information.errorSetSaveActAsValuator")
						: getTranslateByKey("information.errorRemoveSaveActAsValuator"),
				);
			})
			.finally(() => {});
	}

	// #endregion

	// #region Render Constants
	const hasChanges = checkParametersChange(dataParametersScreen, parameterTypes ? lowercaseValues(parameterTypes) : []);

	const rowHeadersActSupervisor = [getTranslateByKey("information.function"), getTranslateByKey("information.actions")];
	// #endregion

	return (
		<>
			<S.Container>
				<S.ContainerSection>
					<SP.HeaderText>{getTranslateByKey("information.registerInsurerParameters")}</SP.HeaderText>

					<SectionParameter
						dataParameterAPI={parameterTypes}
						dataParametersScreen={dataParametersScreen}
						setDataParametersScreen={setDataParametersScreen}
					/>
				</S.ContainerSection>

				<CompanyManufacturer guidCompany={guidCompany} watcherCountry={watcherCountry} />

				<S.ContainerSection>
					<BoxGrid rowHeaders={rowHeadersActSupervisor}>
						<KOGTableRow>
							<TableCell width="25%">{getTranslateByKey("information.ActAsSupervisor")}</TableCell>
							<TableCell width="5%">
								<Switch
									name="actSupervisor"
									color="primary"
									checked={actAsSupervisor}
									onChange={(event, checked) => handleActSupervisor(event, checked)}
								/>
							</TableCell>
						</KOGTableRow>
					</BoxGrid>
				</S.ContainerSection>

				{actAsSupervisor && <SupervisorBodyShopGrid guidCompany={guidCompany} />}

				<S.FooterButtons>
					<SecondaryButton type="button" onClick={handleBack} id="id-valuatorClaimSettings-button-back">
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<Button
						id="id-valuatorClaimSettings-button-save"
						type="submit"
						disabled={!hasChanges || isLoadingSaveSettings}
						isLoading={isLoadingSaveSettings}
						onClick={handleSaveSettings}
					>
						{getTranslateByKey("information.save")}
					</Button>
				</S.FooterButtons>
			</S.Container>
			<ModalConfirmation />
			<ModalConfirmationReason />
		</>
	);
}

export default TabValuatorClaimSettings;
